import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BackHandler, KeyboardAvoidingView, Platform, ScrollView, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { Theme, ThemeConsumer } from "@rneui/themed";
import { FormikErrors, useFormik } from "formik";
import Popover from "react-native-popover-view";
import { shallowEqual } from "react-redux";
import { ZenButton, ZenCheckBox, ZenDivider, ZenIcon, ZenInput, ZenSwitch, ZenText, ZenToast } from "zen-core-elements";

// common components
import CommonHeader from "../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../shared/commonComponents/commonDialogBox";
import ZenSkeleton from "../../shared/commonComponents/zenSkeleton";

import { getScreenPermission, useAppDispatch, useAppSelector } from "../../redux/hooks";
import { pageDirty } from "../../redux/slices/commonSlice";
import { useAppNavigation } from "../../types/@react-navigation/navigation";
import useProductSettings from "../../shared/hooks/useProductSettings";
import { peekNextSkuNumber, previewSku, resetSkuCounter, sanitizePrefix } from "../../utils/productSettingsStorage";
import { buildSampleLabelData } from "../../utils/productLabel";
import {
    ALWAYS_PRINTED_NOTE,
    CUSTOM_LAYOUT_FIELDS,
    CUSTOM_LAYOUT_GROUPS,
    CustomLabelLayout,
    CustomLayoutNumericKey,
    LABEL_FIELD_OPTIONS,
    LABEL_LAYOUT_OPTIONS,
    LabelFieldKey,
    LabelFieldOption,
    ProductSettings as ProductSettingsType,
    SEPARATOR_OPTIONS,
    SKU_PREFIX_MAX_LENGTH,
    SKU_SEQUENCE_MAX_LENGTH,
    SKU_SEQUENCE_MIN_LENGTH,
    SKU_START_NUMBER_MAX,
    SKU_START_NUMBER_MIN,
    SKU_TOKEN_OPTIONS,
    SkuSeparator,
} from "./productSettings.module";

interface SkuFieldErrors {
    prefix?: string;
    sequenceLength?: string;
    startNumber?: string;
}

type CustomFieldErrors = Partial<Record<CustomLayoutNumericKey, string>>;

type CustomLayoutText = Record<CustomLayoutNumericKey, string>;

const customLayoutToText = (custom: CustomLabelLayout): CustomLayoutText =>
    CUSTOM_LAYOUT_FIELDS.reduce((text, field) => {
        text[field.key] = String(custom?.[field.key] ?? "");
        return text;
    }, {} as CustomLayoutText);

/** Only the Custom layout's dimensions are user entered, so only they validate. */
const validateCustomLayout = (values: ProductSettingsType): CustomFieldErrors => {
    if (values.label.layout !== "CUSTOM") {
        return {};
    }

    return CUSTOM_LAYOUT_FIELDS.reduce<CustomFieldErrors>((errors, field) => {
        const value = values.label.custom?.[field.key];
        if (!Number.isFinite(value) || value < field.min || value > field.max) {
            errors[field.key] = `Enter ${field.min} to ${field.max}`;
        }
        return errors;
    }, {});
};

const validateSettings = (values: ProductSettingsType): FormikErrors<ProductSettingsType> => {
    const custom = validateCustomLayout(values);
    const label = Object.keys(custom).length > 0 ? { label: { custom } } : {};

    // Nothing downstream depends on the SKU fields while auto SKU is off.
    if (!values.sku.enabled) {
        return label as FormikErrors<ProductSettingsType>;
    }

    const sku: SkuFieldErrors = {};
    if (values.sku.prefix.length > SKU_PREFIX_MAX_LENGTH) {
        sku.prefix = `Prefix can be at most ${SKU_PREFIX_MAX_LENGTH} characters`;
    }
    if (
        !Number.isFinite(values.sku.sequenceLength) ||
        values.sku.sequenceLength < SKU_SEQUENCE_MIN_LENGTH ||
        values.sku.sequenceLength > SKU_SEQUENCE_MAX_LENGTH
    ) {
        sku.sequenceLength = `Enter a value between ${SKU_SEQUENCE_MIN_LENGTH} and ${SKU_SEQUENCE_MAX_LENGTH}`;
    }
    if (
        !Number.isFinite(values.sku.startNumber) ||
        values.sku.startNumber < SKU_START_NUMBER_MIN ||
        values.sku.startNumber > SKU_START_NUMBER_MAX
    ) {
        sku.startNumber = `Enter a number from ${SKU_START_NUMBER_MIN}`;
    }

    return Object.keys(sku).length > 0 ? { sku } : {};
};

const ProductSettings = () => {
    const { isWrite: isSettingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual);
    const navigation = useAppNavigation();
    const dispatch = useAppDispatch();
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    const { settings, loading, error, save, reset } = useProductSettings();

    const [nextSequence, setNextSequence] = useState<number | null>(null);
    const [leaveVisible, setLeaveVisible] = useState(false);
    const [resetCounterVisible, setResetCounterVisible] = useState(false);
    const [resetDefaultsVisible, setResetDefaultsVisible] = useState(false);
    const [saving, setSaving] = useState(false);

    const formik = useFormik<ProductSettingsType>({
        initialValues: settings,
        enableReinitialize: true,
        validate: validateSettings,
        validateOnMount: false,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // The numeric inputs keep their own text so clearing the field does not
    // immediately render a "0" under the caret.
    const [sequenceText, setSequenceText] = useState(String(settings.sku.sequenceLength));
    const [startNumberText, setStartNumberText] = useState(String(settings.sku.startNumber));

    const [customText, setCustomText] = useState<CustomLayoutText>(() =>
        customLayoutToText(settings.label.custom),
    );

    useEffect(() => {
        setSequenceText(String(settings.sku.sequenceLength));
        setStartNumberText(String(settings.sku.startNumber));
        setCustomText(customLayoutToText(settings.label.custom));
    }, [settings]);

    const refreshNextSequence = useCallback(async (current: ProductSettingsType) => {
        try {
            setNextSequence(await peekNextSkuNumber(current));
        } catch {
            setNextSequence(null);
        }
    }, []);

    useEffect(() => {
        if (!loading) {
            refreshNextSequence(settings);
        }
    }, [loading, settings, refreshNextSequence]);

    const isDirty = formik.dirty;

    useEffect(() => {
        dispatch(pageDirty(isDirty));
        return () => {
            dispatch(pageDirty(false));
        };
    }, [isDirty, dispatch]);

    const onLeave = useCallback(() => {
        if (formik.dirty) {
            setLeaveVisible(true);
            return true;
        }
        navigation.goBack();
        return true;
    }, [formik.dirty, navigation]);

    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", onLeave);
        return () => subscription.remove();
    }, [onLeave]);

    const onSave = async (values: ProductSettingsType) => {
        setSaving(true);
        try {
            const saved = await save(values);
            formik.resetForm({ values: saved });
            dispatch(pageDirty(false));
            await refreshNextSequence(saved);
            ZenToast({ message: "Product settings saved successfully", type: "success" });
        } catch {
            ZenToast({ message: "Unable to save the product settings", type: "error" });
        } finally {
            setSaving(false);
        }
    };

    const onResetDefaults = async () => {
        setResetDefaultsVisible(false);
        setSaving(true);
        try {
            const defaults = await reset();
            formik.resetForm({ values: defaults });
            dispatch(pageDirty(false));
            await refreshNextSequence(defaults);
            ZenToast({ message: "Product settings restored to defaults", type: "success" });
        } catch {
            ZenToast({ message: "Unable to reset the product settings", type: "error" });
        } finally {
            setSaving(false);
        }
    };

    const onResetCounter = async () => {
        setResetCounterVisible(false);
        try {
            // Resets against the saved starting number, not the unsaved edit.
            const next = await resetSkuCounter(settings);
            setNextSequence(next);
            ZenToast({ message: "SKU counter reset", type: "success" });
        } catch {
            ZenToast({ message: "Unable to reset the SKU counter", type: "error" });
        }
    };

    const { setFieldValue, values, errors } = formik;
    const skuErrors = (errors?.sku ?? {}) as SkuFieldErrors;
    const skuEnabled = values.sku.enabled;
    const selectedFields = values.label.fields;

    const customErrors = ((errors?.label as { custom?: CustomFieldErrors })?.custom ?? {}) as CustomFieldErrors;

    /**
     * Numeric inputs keep their own text so a half typed "12." or a cleared
     * field does not fight the caret. Formik always holds the parsed number.
     */
    const onCustomFieldChange = useCallback(
        (key: CustomLayoutNumericKey, raw: string, allowDecimal: boolean) => {
            const cleaned = allowDecimal
                ? raw.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
                : raw.replace(/[^0-9]/g, "");

            setCustomText((previous) => ({ ...previous, [key]: cleaned }));
            const parsed = allowDecimal ? parseFloat(cleaned) : parseInt(cleaned, 10);
            setFieldValue(`label.custom.${key}`, Number.isFinite(parsed) ? parsed : NaN);
        },
        [setFieldValue],
    );

    const setLabelFields = useCallback(
        (fields: LabelFieldKey[]) => setFieldValue("label.fields", fields),
        [setFieldValue],
    );

    const toggleLabelField = useCallback(
        (key: LabelFieldKey) => {
            const isSelected = selectedFields.includes(key);
            if (!isSelected) {
                setLabelFields([...selectedFields, key]);
                return;
            }
            if (selectedFields.length === 1) {
                ZenToast({ message: "At least one field must be printed on the label", type: "warning" });
                return;
            }
            setLabelFields(selectedFields.filter((field) => field !== key));
        },
        [selectedFields, setLabelFields],
    );

    const moveLabelField = useCallback(
        (key: LabelFieldKey, direction: -1 | 1) => {
            const index = selectedFields.indexOf(key);
            const target = index + direction;
            if (index < 0 || target < 0 || target >= selectedFields.length) {
                return;
            }
            const reordered = [...selectedFields];
            reordered[index] = reordered[target];
            reordered[target] = key;
            setLabelFields(reordered);
        },
        [selectedFields, setLabelFields],
    );

    const selectedOptions = useMemo(
        () =>
            selectedFields
                .map((key) => LABEL_FIELD_OPTIONS.find((option) => option.key === key))
                .filter((option): option is LabelFieldOption => Boolean(option)),
        [selectedFields],
    );

    const availableOptions = useMemo(
        () => LABEL_FIELD_OPTIONS.filter((option) => !selectedFields.includes(option.key)),
        [selectedFields],
    );

    const labelPreview = useMemo(() => buildSampleLabelData(selectedFields), [selectedFields]);

    const skuPreview = useMemo(() => previewSku(values), [values]);

    const hasErrors = Object.keys(errors ?? {}).length > 0;
    const canSave = isSettingWrite && isDirty && !hasErrors && !saving;

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => {
                const style = theme?.productSettingsStyle;

                const renderSectionHeader = (title: string, subTitle: string) => (
                    <>
                        <ZenText content={title} size={"large"} color={theme?.primary} bold style={style?.cardTitle} />
                        <ZenText
                            content={subTitle}
                            size={theme?.smallHeaderFontSize}
                            color={theme?.grey}
                            bold
                            style={style?.cardSubTitle}
                        />
                    </>
                );

                const renderSwitchRow = (
                    label: string,
                    value: boolean,
                    onChange: () => void,
                    disabled?: boolean,
                    helpText?: string,
                ) => (
                    <View
                        style={style?.row}
                        accessible
                        accessibilityRole="switch"
                        accessibilityLabel={label}
                        accessibilityState={{ checked: value, disabled: Boolean(disabled) }}
                    >
                        <View style={style?.rowLabelWrapper}>
                            <ZenText content={label} size={"medium"} color={theme?.lightDark} />
                            {Boolean(helpText) && (
                                <ZenText
                                    content={helpText}
                                    size={theme?.smallHeaderFontSize}
                                    color={theme?.grey}
                                    style={style?.helperText}
                                />
                            )}
                        </View>
                        <ZenSwitch
                            value={value}
                            activeColor={theme?.primary}
                            disabled={disabled}
                            activeThumbColor={theme?.light}
                            inActiveColor={theme?.borderColor}
                            inactiveThumbColor={theme?.light}
                            onValueChange={onChange}
                        />
                    </View>
                );

                const renderLabelRow = (option: LabelFieldOption, order?: number) => {
                    const isSelected = order !== undefined;
                    const disabled = !isSettingWrite;
                    return (
                        <View
                            key={option.key}
                            style={style?.row}
                            accessible
                            accessibilityRole="checkbox"
                            accessibilityLabel={option.label}
                            accessibilityState={{ checked: isSelected, disabled }}
                        >
                            <View style={[style?.rowLabelWrapper, { flexDirection: "row", alignItems: "center" }]}>
                                {isSelected && (
                                    <View style={style?.orderBadge}>
                                        <ZenText
                                            content={String(order! + 1)}
                                            size={theme?.badgeFontSize}
                                            color={theme?.light}
                                            bold
                                        />
                                    </View>
                                )}
                                <ZenCheckBox
                                    title={option.label}
                                    checked={isSelected}
                                    disabled={disabled}
                                    checkedColor={theme?.primary}
                                    unCheckedColor={theme?.grey}
                                    size={theme?.iconSize}
                                    testID={`label-field-${option.key}`}
                                    titleStyle={{ fontSize: theme?.smallerFontSize, color: theme?.lightDark, fontFamily: theme?.medium }}
                                    containerStyle={{ backgroundColor: theme?.transparent, paddingVertical: 0 }}
                                    onPress={() => toggleLabelField(option.key)}
                                />
                            </View>
                            {isSelected && (
                                <View style={style?.reorderActions}>
                                    <TouchableOpacity
                                        style={style?.reorderButton}
                                        disabled={disabled || order === 0}
                                        accessibilityRole="button"
                                        accessibilityLabel={`Move ${option.label} up`}
                                        onPress={() => moveLabelField(option.key, -1)}
                                    >
                                        <ZenIcon
                                            name="arrow-up"
                                            type="material-community"
                                            size={theme?.iconSize}
                                            color={order === 0 ? theme?.disableTextColor : theme?.primary}
                                        />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={style?.reorderButton}
                                        disabled={disabled || order === selectedOptions.length - 1}
                                        accessibilityRole="button"
                                        accessibilityLabel={`Move ${option.label} down`}
                                        onPress={() => moveLabelField(option.key, 1)}
                                    >
                                        <ZenIcon
                                            name="arrow-down"
                                            type="material-community"
                                            size={theme?.iconSize}
                                            color={
                                                order === selectedOptions.length - 1
                                                    ? theme?.disableTextColor
                                                    : theme?.primary
                                            }
                                        />
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    );
                };

                return (
                    <KeyboardAvoidingView
                        behavior={Platform.OS === "ios" ? "padding" : undefined}
                        enabled
                        style={style?.outerContainer}
                    >
                        <CommonHeader headerName="Product Settings" backIcon backIconOnpress={onLeave} rightComponent={<View style={style?.footer}>
                            {isSettingWrite && (
                                <ZenButton
                                    title="Reset to defaults"
                                    type="outline"
                                    titleColor={theme?.primary}
                                    outlineColor={theme?.primary}
                                    buttonStyle={style?.outlineButton}
                                    containerStyle={[style?.footerButtonContainer, { flex: isLandscape ? 0 : 1 }]}
                                    disabled={saving}
                                    onPress={() => setResetDefaultsVisible(true)}
                                />
                            )}
                            {isSettingWrite && (
                                <ZenButton
                                    title="Save"
                                    titleColor={theme?.light}
                                    buttonStyle={canSave ? style?.primaryButton : style?.disabledButton}
                                    containerStyle={[style?.footerButtonContainer, { flex: isLandscape ? 0 : 1 }]}
                                    disabled={!canSave}
                                    loading={saving}
                                    onPress={() => formik.handleSubmit()}
                                />
                            )}
                        </View>} />

                        {loading ? (
                            <View style={style?.sectionsWrapper}>
                                <View style={[style?.card, style?.skeletonCard]}>
                                    <ZenSkeleton width={"45%"} height={18} />
                                    <ZenSkeleton width={"70%"} height={12} />
                                    <ZenSkeleton height={48} borderRadius={8} />
                                    <ZenSkeleton height={48} borderRadius={8} />
                                    <ZenSkeleton height={48} borderRadius={8} />
                                </View>
                                <View style={[style?.card, style?.skeletonCard]}>
                                    <ZenSkeleton width={"45%"} height={18} />
                                    <ZenSkeleton height={48} borderRadius={8} />
                                    <ZenSkeleton height={48} borderRadius={8} />
                                </View>
                            </View>
                        ) : (
                            <>
                                {Boolean(error) && (
                                    <View style={style?.errorBanner}>
                                        <ZenIcon
                                            name="alert-circle-outline"
                                            type="material-community"
                                            size={theme?.iconSize}
                                            color={theme?.red}
                                        />
                                        <ZenText
                                            content={error}
                                            size={theme?.smallFontSize}
                                            color={theme?.red}
                                            style={{ flex: 1 }}
                                        />
                                    </View>
                                )}

                                <ScrollView
                                    showsVerticalScrollIndicator={false}
                                    keyboardShouldPersistTaps="handled"
                                    keyboardDismissMode="on-drag"
                                    contentContainerStyle={style?.scrollContent}
                                >
                                    <View style={[style?.sectionsWrapper, { flexDirection: isLandscape ? "row" : "column" }]}>
                                        {/* Section A: Automatic SKU generation */}
                                        <View style={[style?.card, { flex: isLandscape ? 1 : undefined }]}>
                                            {renderSectionHeader(
                                                "Automatic SKU Generation",
                                                "Generate a SKU automatically when a new product is created",
                                            )}

                                            {renderSwitchRow(
                                                "Enable auto SKU generation",
                                                skuEnabled,
                                                () => setFieldValue("sku.enabled", !skuEnabled),
                                                !isSettingWrite,
                                            )}

                                            {skuEnabled && (
                                                <>
                                                    <View style={style?.fieldWrapper}>
                                                        <ZenInput
                                                            label="Prefix"
                                                            value={values.sku.prefix}
                                                            disabled={!isSettingWrite}
                                                            editable={isSettingWrite}
                                                            autoCapitalize="characters"
                                                            maxLength={SKU_PREFIX_MAX_LENGTH}
                                                            placeholder="E.g. ZC"
                                                            type="outlined"
                                                            accessibilityLabel="SKU prefix"
                                                            errorMessage={skuErrors?.prefix}
                                                            errorStyle={style?.errorStyle}
                                                            labelStyle={style?.fieldLabel}
                                                            containerStyle={style?.inputContainer}
                                                            inputContainerStyle={style?.inputInnerContainer}
                                                            onChangeText={(text) =>
                                                                setFieldValue("sku.prefix", sanitizePrefix(text))
                                                            }
                                                        />
                                                        <ZenText
                                                            content={`Letters and numbers only, up to ${SKU_PREFIX_MAX_LENGTH} characters`}
                                                            size={theme?.smallHeaderFontSize}
                                                            color={theme?.grey}
                                                            style={style?.helperText}
                                                        />
                                                    </View>

                                                    <View style={style?.fieldWrapper}>
                                                        <ZenText content="Separator" style={style?.fieldLabel} />
                                                        <View style={style?.separatorRow}>
                                                            {SEPARATOR_OPTIONS.map((option) => (
                                                                <ZenCheckBox
                                                                    key={option.label}
                                                                    title={option.label}
                                                                    checked={values.sku.separator === option.value}
                                                                    disabled={!isSettingWrite}
                                                                    checkedIcon="dot-circle-o"
                                                                    unCheckedIcon="circle-o"
                                                                    checkedColor={theme?.primary}
                                                                    unCheckedColor={theme?.grey}
                                                                    size={theme?.iconSize}
                                                                    testID={`separator-${option.value || "none"}`}
                                                                    titleStyle={{
                                                                        fontSize: theme?.smallerFontSize,
                                                                        color: theme?.lightDark,
                                                                        fontFamily: theme?.medium,
                                                                    }}
                                                                    containerStyle={{ backgroundColor: theme?.transparent }}
                                                                    onPress={() =>
                                                                        setFieldValue(
                                                                            "sku.separator",
                                                                            option.value as SkuSeparator,
                                                                        )
                                                                    }
                                                                />
                                                            ))}
                                                        </View>
                                                    </View>

                                                    <View style={style?.fieldWrapper}>
                                                        <ZenText content="Include in SKU" style={style?.fieldLabel} />
                                                        <View style={style?.rowGroup}>
                                                            {SKU_TOKEN_OPTIONS.map((token) =>
                                                                <React.Fragment key={token.key}>
                                                                    {renderSwitchRow(
                                                                        token.label,
                                                                        values.sku[token.key],
                                                                        () => setFieldValue(`sku.${token.key}`, !values.sku[token.key]),
                                                                        !isSettingWrite,
                                                                        token.helpText,
                                                                    )}
                                                                </React.Fragment>,
                                                            )}
                                                        </View>
                                                    </View>

                                                    <View style={style?.fieldWrapper}>
                                                        <ZenInput
                                                            label="Sequence length"
                                                            value={sequenceText}
                                                            disabled={!isSettingWrite}
                                                            editable={isSettingWrite}
                                                            keyboardType="number-pad"
                                                            maxLength={1}
                                                            type="outlined"
                                                            accessibilityLabel="SKU sequence length"
                                                            errorMessage={skuErrors?.sequenceLength}
                                                            errorStyle={style?.errorStyle}
                                                            labelStyle={style?.fieldLabel}
                                                            containerStyle={style?.inputContainer}
                                                            inputContainerStyle={style?.inputInnerContainer}
                                                            onChangeText={(text) => {
                                                                const digits = text.replace(/[^0-9]/g, "");
                                                                setSequenceText(digits);
                                                                setFieldValue(
                                                                    "sku.sequenceLength",
                                                                    digits === "" ? 0 : parseInt(digits, 10),
                                                                );
                                                            }}
                                                        />
                                                        <ZenText
                                                            content={`${SKU_SEQUENCE_MIN_LENGTH} to ${SKU_SEQUENCE_MAX_LENGTH} digits, zero padded`}
                                                            size={theme?.smallHeaderFontSize}
                                                            color={theme?.grey}
                                                            style={style?.helperText}
                                                        />
                                                    </View>

                                                    <View style={style?.fieldWrapper}>
                                                        <ZenInput
                                                            label="Starting number"
                                                            value={startNumberText}
                                                            disabled={!isSettingWrite}
                                                            editable={isSettingWrite}
                                                            keyboardType="number-pad"
                                                            maxLength={9}
                                                            type="outlined"
                                                            accessibilityLabel="SKU starting number"
                                                            errorMessage={skuErrors?.startNumber}
                                                            errorStyle={style?.errorStyle}
                                                            labelStyle={style?.fieldLabel}
                                                            containerStyle={style?.inputContainer}
                                                            inputContainerStyle={style?.inputInnerContainer}
                                                            onChangeText={(text) => {
                                                                const digits = text.replace(/[^0-9]/g, "");
                                                                setStartNumberText(digits);
                                                                setFieldValue(
                                                                    "sku.startNumber",
                                                                    digits === "" ? 0 : parseInt(digits, 10),
                                                                );
                                                            }}
                                                        />
                                                    </View>

                                                    <View style={style?.counterRow}>
                                                        <View style={style?.rowLabelWrapper}>
                                                            <ZenText
                                                                content="Next sequence number"
                                                                size={theme?.smallerFontSize}
                                                                color={theme?.labelColor}
                                                            />
                                                            <ZenText
                                                                content={nextSequence === null ? "--" : String(nextSequence)}
                                                                size={"medium"}
                                                                color={theme?.dark}
                                                                bold
                                                                accessibilityLabel={`Next sequence number ${nextSequence ?? "unavailable"}`}
                                                            />
                                                        </View>
                                                        {isSettingWrite && (
                                                            <ZenButton
                                                                title="Reset counter"
                                                                type="outline"
                                                                titleColor={theme?.primary}
                                                                outlineColor={theme?.primary}
                                                                buttonStyle={style?.outlineButton}
                                                                containerStyle={style?.footerButtonContainer}
                                                                onPress={() => setResetCounterVisible(true)}
                                                            />
                                                        )}
                                                    </View>

                                                    <View style={style?.previewCard}>
                                                        <ZenText
                                                            content="Preview"
                                                            size={theme?.smallHeaderFontSize}
                                                            color={theme?.primary}
                                                            bold
                                                        />
                                                        <ZenText
                                                            content={skuPreview}
                                                            size={"large"}
                                                            color={theme?.dark}
                                                            bold
                                                            style={style?.previewValue}
                                                            accessibilityLabel={`Sample SKU ${skuPreview}`}
                                                        />
                                                    </View>
                                                </>
                                            )}
                                        </View>

                                        {/* Section B: Label printer content */}
                                        <View style={[style?.card, { flex: isLandscape ? 1 : undefined }]}>
                                            {renderSectionHeader(
                                                "Label Printer Content",
                                                "Choose which details print on the product label",
                                            )}

                                            <View style={style?.infoNote}>
                                                <ZenIcon
                                                    name="information-outline"
                                                    type="material-community"
                                                    size={theme?.iconSize}
                                                    color={theme?.primary}
                                                />
                                                <ZenText
                                                    content={ALWAYS_PRINTED_NOTE}
                                                    size={theme?.smallHeaderFontSize}
                                                    color={theme?.lightDark}
                                                    style={{ flex: 1 }}
                                                />
                                            </View>

                                            <ZenText content="Selected fields (print order)" style={style?.fieldLabel} />
                                            <View style={style?.rowGroup}>
                                                {selectedOptions.map((option, index) => renderLabelRow(option, index))}
                                            </View>

                                            {availableOptions.length > 0 && (
                                                <>
                                                    <ZenText
                                                        content="Available fields"
                                                        style={[style?.fieldLabel, { marginTop: 16 }]}
                                                    />
                                                    <View style={style?.rowGroup}>
                                                        {availableOptions.map((option) => renderLabelRow(option))}
                                                    </View>
                                                </>
                                            )}

                                            <ZenText
                                                content="Label sheet layout"
                                                style={[style?.fieldLabel, { marginTop: 16 }]}
                                            />
                                            <View style={style?.rowGroup}>
                                                {LABEL_LAYOUT_OPTIONS.map((option) => (
                                                    <View
                                                        key={option.key}
                                                        style={style?.row}
                                                        accessible
                                                        accessibilityRole="radio"
                                                        accessibilityLabel={`${option.label}. ${option.description}`}
                                                        accessibilityState={{
                                                            checked: values.label.layout === option.key,
                                                            disabled: !isSettingWrite,
                                                        }}
                                                    >
                                                        <View style={style?.rowLabelWrapper}>
                                                            <ZenCheckBox
                                                                title={option.label}
                                                                checked={values.label.layout === option.key}
                                                                disabled={!isSettingWrite}
                                                                checkedIcon="dot-circle-o"
                                                                unCheckedIcon="circle-o"
                                                                checkedColor={theme?.primary}
                                                                unCheckedColor={theme?.grey}
                                                                size={theme?.iconSize}
                                                                testID={`label-layout-${option.key}`}
                                                                titleStyle={{
                                                                    fontSize: theme?.smallerFontSize,
                                                                    color: theme?.lightDark,
                                                                    fontFamily: theme?.medium,
                                                                }}
                                                                containerStyle={{
                                                                    backgroundColor: theme?.transparent,
                                                                    paddingVertical: 0,
                                                                }}
                                                                onPress={() => setFieldValue("label.layout", option.key)}
                                                            />
                                                            <ZenText
                                                                content={option.description}
                                                                size={theme?.smallHeaderFontSize}
                                                                color={theme?.grey}
                                                                style={style?.helperText}
                                                            />
                                                        </View>
                                                    </View>
                                                ))}
                                            </View>

                                            {/* Dimension inputs, only for the Custom layout */}
                                            {values.label.layout === "CUSTOM" && (
                                                <View style={style?.customLayoutCard}>
                                                    {CUSTOM_LAYOUT_GROUPS.map((group) => (
                                                        <View key={group} style={style?.customGroup}>
                                                            <ZenText
                                                                content={group}
                                                                size={theme?.smallerFontSize}
                                                                color={theme?.primary}
                                                                bold
                                                            />
                                                            <View style={style?.customGrid}>
                                                                {CUSTOM_LAYOUT_FIELDS.filter(
                                                                    (field) => field.group === group,
                                                                ).map((field) => (
                                                                    <View key={field.key} style={style?.customField}>
                                                                        <ZenInput
                                                                            label={
                                                                                field.unit === "mm"
                                                                                    ? `${field.label} (mm)`
                                                                                    : field.label
                                                                            }
                                                                            value={customText[field.key] ?? ""}
                                                                            disabled={!isSettingWrite}
                                                                            editable={isSettingWrite}
                                                                            keyboardType={
                                                                                field.allowDecimal ? "decimal-pad" : "number-pad"
                                                                            }
                                                                            maxLength={6}
                                                                            type="outlined"
                                                                            accessibilityLabel={`${field.label}, ${field.helpText}`}
                                                                            errorMessage={customErrors[field.key]}
                                                                            errorStyle={style?.errorStyle}
                                                                            labelStyle={style?.fieldLabel}
                                                                            containerStyle={style?.inputContainer}
                                                                            inputContainerStyle={style?.inputInnerContainer}
                                                                            onChangeText={(text) =>
                                                                                onCustomFieldChange(
                                                                                    field.key,
                                                                                    text,
                                                                                    field.allowDecimal,
                                                                                )
                                                                            }
                                                                        />
                                                                        <ZenText
                                                                            content={field.helpText}
                                                                            size={theme?.smallHeaderFontSize}
                                                                            color={theme?.grey}
                                                                            style={style?.helperText}
                                                                        />
                                                                    </View>
                                                                ))}
                                                            </View>
                                                        </View>
                                                    ))}

                                                    {renderSwitchRow(
                                                        "Show cut guides",
                                                        values.label.custom.showCutGuides,
                                                        () =>
                                                            setFieldValue(
                                                                "label.custom.showCutGuides",
                                                                !values.label.custom.showCutGuides,
                                                            ),
                                                        !isSettingWrite,
                                                        "Dashed outline around each label, to cut along",
                                                    )}
                                                </View>
                                            )}

                                            <View style={style?.previewCard}>
                                                <ZenText
                                                    content="Label preview"
                                                    size={theme?.smallHeaderFontSize}
                                                    color={theme?.primary}
                                                    bold
                                                />
                                                <View style={{ marginTop: 8 }}>
                                                    {labelPreview.map((item) => (
                                                        <View key={item.key} style={style?.labelPreviewRow}>
                                                            <ZenText
                                                                content={item.label}
                                                                size={theme?.smallHeaderFontSize}
                                                                color={theme?.grey}
                                                            />
                                                            <ZenText
                                                                content={item.value}
                                                                size={theme?.smallFontSize}
                                                                color={theme?.dark}
                                                                bold
                                                            />
                                                        </View>
                                                    ))}
                                                </View>
                                                <View style={style?.barcodePlaceholder}>
                                                    <ZenIcon
                                                        name="barcode"
                                                        type="material-community"
                                                        size={36}
                                                        color={theme?.dark}
                                                    />
                                                    <ZenText
                                                        content={previewSku(values)}
                                                        size={theme?.smallHeaderFontSize}
                                                        color={theme?.dark}
                                                    />
                                                </View>
                                            </View>
                                        </View>
                                    </View>
                                </ScrollView>

                                <ZenDivider style={{ height: 0.6, backgroundColor: theme?.borderColor }} />

                            </>
                        )}

                        {/* Unsaved changes confirmation */}
                        <Popover
                            isVisible={leaveVisible}
                            popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                            onRequestClose={() => setLeaveVisible(false)}
                        >
                            <CommonDialogBox
                                type="EDIT"
                                replaceValue="page"
                                confirmation={(item: { id: number }) => {
                                    setLeaveVisible(false);
                                    if (item?.id === 2) {
                                        dispatch(pageDirty(false));
                                        navigation.goBack();
                                    }
                                }}
                            />
                        </Popover>

                        {/* Reset counter confirmation */}
                        <Popover
                            isVisible={resetCounterVisible}
                            popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                            onRequestClose={() => setResetCounterVisible(false)}
                        >
                            <CommonDialogBox
                                type="OTHER"
                                dialog={{
                                    content: `Reset the SKU counter back to ${settings.sku.startNumber}? Previously generated SKUs are not changed.`,
                                    button: [
                                        { id: 2, name: "Yes" },
                                        { id: 1, name: "No" },
                                    ],
                                }}
                                confirmation={(item: { id: number }) => {
                                    if (item?.id === 2) {
                                        onResetCounter();
                                    } else {
                                        setResetCounterVisible(false);
                                    }
                                }}
                            />
                        </Popover>

                        {/* Reset to defaults confirmation */}
                        <Popover
                            isVisible={resetDefaultsVisible}
                            popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}
                            onRequestClose={() => setResetDefaultsVisible(false)}
                        >
                            <CommonDialogBox
                                type="OTHER"
                                dialog={{
                                    content: "Restore the default product settings? The SKU counter is not affected.",
                                    button: [
                                        { id: 2, name: "Yes" },
                                        { id: 1, name: "No" },
                                    ],
                                }}
                                confirmation={(item: { id: number }) => {
                                    if (item?.id === 2) {
                                        onResetDefaults();
                                    } else {
                                        setResetDefaultsVisible(false);
                                    }
                                }}
                            />
                        </Popover>
                    </KeyboardAvoidingView>
                );
            }}
        </ThemeConsumer>
    );
};

export default ProductSettings;
